import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { collection, doc, orderBy, query } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useDocRow, useDocs } from '@/lib/useDocs';
import { CompetencyPicker } from '@/components/CompetencyPicker';
import { messageForAuthError } from '@/lib/authErrors';
import { formatDate, stageLabel } from '@/lib/format';
import type { Candidate, Competency } from '@/types/domain';
import { strings } from '@/strings';

const saveInterview = callable<Record<string, unknown>, { candidateId: string; stage: string }>('saveInterview');
const vetCandidate = callable<{ candidateId: string }, { candidateId: string; stage: string }>('vetCandidate');
const addCandidateNote = callable<{ candidateId: string; body: string; kind: string }, { noteId: string }>('addCandidateNote');
const logCandidateView = callable<{ candidateId: string }, { logged: boolean }>('logCandidateView');

type Note = { author: string; at: number; body: string; kind: string };

const SECTORS = [
  { value: 'private', label: strings.sectorPrivate },
  { value: 'government', label: strings.sectorGovernment },
  { value: 'academic', label: strings.sectorAcademic },
  { value: 'other', label: strings.sectorOther },
];

const MODES = [
  { value: 'onsite', label: strings.modeOnsite },
  { value: 'remote', label: strings.modeRemote },
  { value: 'hybrid', label: strings.modeHybrid },
  { value: 'field', label: strings.modeField },
] as const;

// مرونة الوقت is a property of the OPPORTUNITY, not of the candidate — it is
// field 19 of بطاقة توصيف الفرصة — so its editor lives there now.

/**
 * النطاقات — the five Riyadh zones plus a residence-only answer. Matching is
 * intra-Riyadh, so this is what the old free-text city field became.
 */
const ZONES = [
  { value: 'north', label: strings.zoneNorth },
  { value: 'east', label: strings.zoneEast },
  { value: 'west', label: strings.zoneWest },
  { value: 'south', label: strings.zoneSouth },
  { value: 'central', label: strings.zoneCentral },
  { value: 'outside', label: strings.zoneOutside },
] as const;

export function CandidateDetailPage() {
  const { id = '' } = useParams();
  const { row, loading, error } = useDocRow<Candidate>(id ? doc(db, 'candidates', id) : null, [id]);
  const { rows: notes } = useDocs<Note>(
    id ? query(collection(db, `candidates/${id}/notes`), orderBy('at', 'desc')) : null,
    [id]
  );

  const [form, setForm] = useState<Candidate | null>(null);
  const [competencies, setCompetencies] = useState<Competency[]>([]);
  const [noteBody, setNoteBody] = useState('');
  const [noteKind, setNoteKind] = useState('screening');
  const [message, setMessage] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Opening a profile is a read of someone's personal data, and the spec
  // promises every one of those is answerable later. Firestore has no read
  // trigger, so the client says so.
  useEffect(() => {
    if (id) logCandidateView({ candidateId: id }).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (row && !form) {
      setForm(row);
      setCompetencies(row.competencies || []);
    }
  }, [row, form]);

  if (loading) return <p className="text-muted">{strings.loading}</p>;
  if (error || !row || !form) return <p role="alert" className="text-danger">{strings.loadFailed}</p>;

  const current = form;

  async function act(fn: () => Promise<unknown>, okMessage: string) {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    setMessage(null);
    try {
      await fn();
      setMessage(okMessage);
    } catch (err) {
      setProblem(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  async function onSave(event: FormEvent) {
    event.preventDefault();
    await act(() => saveInterview({
      candidateId: id,
      profile: {
        currentRole: current.currentRole, employer: current.employer, sector: current.sector,
        supervisionYears: Number(current.supervisionYears) || 0, bio: current.bio,
        zone: current.zone, district: current.district,
      },
      competencies,
      availability: current.availability,
      interests: current.interests,
      modes: current.modes || [],
      travelZones: current.travelZones || [],
    }), strings.candidateSaved);
  }

  const consentLine = row.consent?.withdrawnAt
    ? strings.consentWithdrawn
    : row.consent?.granted ? strings.consentGranted : strings.consentPending;

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <header className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-xl font-bold text-primary">{row.name}</h1>
        <span className="text-sm text-muted">{stageLabel(row.stage)}</span>
        <span className={row.consent?.granted ? 'text-sm text-success' : 'text-sm text-warning'}>{consentLine}</span>
      </header>

      <form onSubmit={onSave} className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-white p-4">
        <h2 className="font-semibold text-ink">{strings.candidateInterviewTitle}</h2>

        <label className="flex flex-col gap-1 text-sm" htmlFor="years">
          {strings.fieldSupervisionYears}
          <input id="years" type="number" min={0} value={current.supervisionYears}
            onChange={(e) => setForm({ ...current, supervisionYears: Number(e.target.value) })}
            className="rounded-md border border-border-subtle px-3 py-2" />
        </label>

        <label className="flex flex-col gap-1 text-sm" htmlFor="zone">
          {strings.fieldZone}
          <select id="zone" value={current.zone}
            onChange={(e) => setForm({ ...current, zone: e.target.value as Candidate['zone'] })}
            className="rounded-md border border-border-subtle px-3 py-2">
            <option value="">—</option>
            {ZONES.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm" htmlFor="district">
          {strings.fieldDistrict}
          <input id="district" value={current.district}
            onChange={(e) => setForm({ ...current, district: e.target.value })}
            className="rounded-md border border-border-subtle px-3 py-2" />
        </label>

        <label className="flex flex-col gap-1 text-sm" htmlFor="sector">
          {strings.fieldSector}
          <select id="sector" value={current.sector}
            onChange={(e) => setForm({ ...current, sector: e.target.value })}
            className="rounded-md border border-border-subtle px-3 py-2">
            {SECTORS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm" htmlFor="bio">
          {strings.fieldBio}
          <textarea id="bio" rows={3} value={current.bio}
            onChange={(e) => setForm({ ...current, bio: e.target.value })}
            className="rounded-md border border-border-subtle px-3 py-2" />
        </label>

        <label className="flex flex-col gap-1 text-sm" htmlFor="hours">
          {strings.fieldHours}
          <input id="hours" type="number" min={0} value={current.availability.hoursPerWeek}
            onChange={(e) => setForm({
              ...current,
              availability: { ...current.availability, hoursPerWeek: Number(e.target.value) },
            })}
            className="rounded-md border border-border-subtle px-3 py-2" />
        </label>

        <fieldset className="flex flex-col gap-1 text-sm">
          <legend className="mb-1">{strings.fieldModes}</legend>
          {MODES.map((m) => (
            <label key={m.value} className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                checked={(current.modes || []).includes(m.value)}
                onChange={(e) => setForm({
                  ...current,
                  modes: e.target.checked
                    ? [...(current.modes || []), m.value]
                    : (current.modes || []).filter((x) => x !== m.value),
                })}
              />
              {m.label}
            </label>
          ))}
        </fieldset>

        <fieldset className="flex flex-col gap-1 text-sm">
          <legend className="mb-1">{strings.fieldTravelZones}</legend>
          {ZONES.filter((z) => z.value !== 'outside').map((z) => (
            <label key={z.value} className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                checked={(current.travelZones || []).includes(z.value as Candidate['travelZones'][number])}
                onChange={(e) => setForm({
                  ...current,
                  travelZones: e.target.checked
                    ? [...(current.travelZones || []), z.value as Candidate['travelZones'][number]]
                    : (current.travelZones || []).filter((x) => x !== z.value),
                })}
              />
              {z.label}
            </label>
          ))}
        </fieldset>

        <CompetencyPicker value={competencies} onChange={setCompetencies} />

        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" disabled={busy}
            className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
            {strings.candidateSave}
          </button>
          {row.stage === 'interviewed' ? (
            <button type="button" disabled={busy}
              onClick={() => act(() => vetCandidate({ candidateId: id }), strings.candidateVetted)}
              className="rounded-md border border-primary px-4 py-2 text-primary disabled:opacity-50">
              {strings.candidateVet}
            </button>
          ) : null}
          {message ? <span className="text-sm text-success">{message}</span> : null}
          {problem ? <span role="alert" className="text-sm text-danger">{problem}</span> : null}
        </div>
      </form>

      <section className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-white p-4">
        <h2 className="font-semibold text-ink">{strings.candidateNotesTitle}</h2>
        <label className="flex flex-col gap-1 text-sm" htmlFor="note">
          {strings.candidateAddNote}
          <textarea id="note" rows={2} value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
            className="rounded-md border border-border-subtle px-3 py-2" />
        </label>
        <label className="flex w-40 flex-col gap-1 text-sm" htmlFor="note-kind">
          {strings.candidateNoteKind}
          <select id="note-kind" value={noteKind} onChange={(e) => setNoteKind(e.target.value)}
            className="rounded-md border border-border-subtle px-3 py-2">
            <option value="screening">{strings.noteKindScreening}</option>
            <option value="reference">{strings.noteKindReference}</option>
            <option value="followup">{strings.noteKindFollowup}</option>
          </select>
        </label>
        <button
          type="button"
          disabled={busy || noteBody.trim() === ''}
          onClick={() => act(async () => {
            await addCandidateNote({ candidateId: id, body: noteBody.trim(), kind: noteKind });
            setNoteBody('');
          }, strings.candidateSaved)}
          className="self-start min-h-11 rounded-md border border-border-subtle bg-white px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50"
        >
          {strings.candidateAddNote}
        </button>

        <ul className="flex flex-col gap-2">
          {notes.map((note) => (
            <li key={note.id} className="rounded-md border border-border-subtle p-2 text-sm">
              <span className="text-muted" dir="ltr">{formatDate(note.at)}</span>
              <p className="text-ink">{note.body}</p>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
